<template>
  <div class="data-chart">
    <div class="title">{{ title }}</div>
    <div class="data-chart__content"></div>
    <div class="data-chart__legend" />
    <q-inner-loading :showing="visible">
      <q-spinner size="50px" color="secondary" />
    </q-inner-loading>
    <q-resize-observer :debounce="500" @resize="refreshChart" />
  </div>
</template>

<script>
import bb, { bar, spline } from "billboard.js";
import "billboard.js/dist/theme/insight.css";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatTextLabel } from "@/utils/formatText";

export default {
  name: "LiveDashboardChart",
  props: {
    title: String,
    dataset: String,
    filter: Object,
    height: String,
  },
  data() {
    return {
      visible: true,
      chartData: null,
      interval: null,
      chart: "",
    };
  },

  methods: {
    async fetchData(siteId, networkId, operatorId) {
      // Dataset name is the store procedure name used to return the results.
      const payload = {
          filterOptions: {
            siteIds: siteId ? [siteId] : [],
            networkId,
            operatorId
          },
          report: "live-dashboard",
          dataset: this.$props.dataset.replace(/ /g, "")
        };

      await this.$store.dispatch("fetchData", payload)
        .then((response) => {
          this.chartData = response.data;
          this.visible = false;
        })
        .catch((e) => {
          this.errors.push(e);
        });

      // Generate the chart
      this.chart = bb.generate({
        bindto: ".data-chart__content",
        size: {
          height: this.height,
        },
        padding: {
          top: 20,
          right: 20,
          bottom: 0,
          left: 70,
        },
        // Data Assignment
        data: {
          json: this.chartData,
          xFormat: "%Y-%m-%dT%H:%M:%S.%LZ",
          keys: {
            x: "Datetime",
            value: ["Stake", "Wins", "GGR"],
          },
          groups: [["Stake", "Wins"]],
          types: {
            Stake: bar(),
            Wins: bar(),
            GGR: spline(),
          },
          colors: {
            Stake: "#122389",
            Wins: "#458EDD",
            GGR: "#FD7496",
          },
          // Show data based on click of bar or line
          onclick: function(d, element) {
            console.log("onclick", d, element);
          },
        },
        // Pointer Control
        point: {
          focus: {
            only: true,
          },
        },
        // Chart Display
        bar: {
          padding: 2,
          width: {
            max: 20,
          },
          radius: {
            ratio: 0.1,
          },
        },
        spline: {
          size: 6,
        },
        // Axis Display
        axis: {
          x: {
            type: "timeseries",
            tick: {
              format: "%H:%M:%S",
              show: false,
              text: {
                show: false,
              },
            },
          },
          y: {
            tick: {
              show: false,
              culling: {
                max: 5,
              },
              text: {
                show: true,
              },
              format: (number) => {
                return formatCurrency(number);
              },
            },
          },
          y2: {
            show: false,
            tick: {
              show: false,
              culling: {
                max: 5,
              },
              text: {
                show: true,
              },
            },
          },
        },
        // Additional Lines
        grid: {
          y: {
            show: true,
          },
        },
        // Legend Display
        legend: {
          contents: {
            bindto: ".data-chart__legend",
            template: (title, color) => {
              return title === "GGR"
                ? "<div class='data-chart__legend--category'><span class='legend-item-line' style='background-color:" +
                    color +
                    ";'></span>" +
                    title +
                    "</div>"
                : "<div class='data-chart__legend--category'><span class='legend-item' style='background-color:" +
                    color +
                    ";'></span>" +
                    title +
                    "</div>";
            },
          },
        },
        // Tooltip Display
        tooltip: {
          order: "asce",

          format: {
            name: (name) => {
              return formatTextLabel(name);
            },
          },
        },
      });
    },
    // Resize the chart when the width of the container is changed. I.e menu open then closed.
    refreshChart() {
      if (this.chart) {
        setTimeout(() => {
          try {
            this.chart.resize();
          } catch {
            // Prevents error occuring when container is refreshed before chart object is reached
          }
        }, 100);
      }
    },
  },
  watch: {
    filter: function(filterOption) {
      this.fetchData(
        filterOption.siteId,
        filterOption.networkId,
        filterOption.operatorId
      );
      this.visible = true;

      clearInterval(this.interval);
      this.interval = setInterval(() => {
        this.fetchData(
          filterOption.siteId,
          filterOption.networkId,
          filterOption.operatorId
        );
      }, 180000);
    },
  },
  async mounted() {
    clearInterval(this.interval);
    this.fetchData(
      this.$store.state.siteFilter.id,
      this.$store.state.networkFilter.id,
      this.$store.state.selectedOperator.id
    );
    this.visible = true; //loading gif

    clearInterval(this.interval);
    this.interval = setInterval(() => {
      this.fetchData(
        this.$store.state.siteFilter.id,
        this.$store.state.networkFilter.id,
        this.$store.state.selectedOperator.id
      );
    }, 180000);
  },

  // Prevent Memory Leak
  beforeDestroy() {
    clearInterval(this.interval);
  },
};
</script>
<style lang="scss">
.data-chart {
  overflow: hidden;
  &__content {
    padding-right: 15px;
    width: 100%;
    height: 100%;
    svg {
      margin: auto;
      .domain {
        display: none;
      }
      .bb-axis-y text,
      .bb-axis-y2 text {
        fill: #4c5862;
        font-family: Barlow;
        font-weight: normal;
        font-size: 12px;
        line-height: 1;
      }
      .bb-line {
        stroke-width: 4px;
        box-shadow: 1px 1px 1px black;
      }
      .bb-xgrid-focus line {
        stroke: #c2cfe0;
        stroke-width: 1px;
      }
      .bb-grid line {
        stroke: #e5e5e5;
      }
      .pointer {
        &__outer {
          stroke: none;
          fill: #ffa000;
          opacity: 0.2;
        }
        &__middle {
          stroke: none;
          fill: #ffa000;
          opacity: 0.4;
        }
        &__inner {
          stroke: #ffa000;
          stroke-width: 1;
          fill: #fff;
        }
      }
    }
    .bb-axis-x {
      display: none !important;
    }
    .bb-tooltip {
      border-collapse: separate;
      border-spacing: 0;
      empty-cells: show;
      border: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(50px);
      border-radius: 5px;
      text-align: left;
      font-size: 11px;
      overflow: hidden;
      tr {
        font-family: Barlow;
        font-style: normal;
        font-weight: 600;
        font-size: 12.7397px;
        th {
          font-size: 12px;
          padding: 4px 8px;
          text-align: left;
          border-bottom: 0;
          color: white;
        }

        .name {
          text-align: left;
          background-color: transparent;
          color: white;
        }
        .value {
          text-align: right;
          background-color: transparent;
          color: white;
        }
      }
    }
  }
  &__legend {
    display: flex;
    position: relative;
    bottom: 15px;
    margin: 0 20px;
    font-family: Barlow;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    z-index: 1;
    &--category {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-right: 20px;
      .legend-item {
        display: block;
        height: 15px;
        width: 15px;
        border-radius: 50%;
        margin-right: 8px;
      }
      .legend-item-line {
        display: block;
        height: 3px;
        width: 20px;
        margin-right: 8px;
      }
    }
  }
}
// Dark Mode
.body {
  &--dark {
    .data-chart {
      &__content {
        svg {
          .bb-axis-y text,
          .bb-axis-y2 text {
            fill: #fff;
          }
          .bb-xgrid-focus line {
            stroke: #c2cfe0;
          }
          .bb-grid line {
            stroke: #353535;
          }
        }
        .bb-tooltip {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(50px);
          tr {
            th {
              color: black;
            }
            .name {
              color: black;
            }
            .value {
              color: black;
            }
          }
        }
      }
    }
  }
}
</style>
